import { useEffect, useState, Suspense } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PageLoader } from '@/routes/PageLoader';
import { useSpaInternalNavCapture } from '@/hooks/useSpaInternalNavCapture';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { Frame, Box, InlineStack, Text, Button } from '@/lib/polaris';
import { AdminToastProvider } from '@/contexts/AdminToastContext';
import { CallCenterProvider } from '@/contexts/CallCenterContext';
import { UnsavedChangesProvider, useUnsavedChanges } from '@/contexts/UnsavedChangesContext';
import { AdminGlobalSearch } from '@/components/polaris/AdminGlobalSearch';
import { FloatingDialerWidget } from '@/components/callCenter/FloatingDialerWidget';
import { ErrorBoundarySection } from '@/components/ErrorBoundarySection';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import { useFeatureFlags } from '@smarty/shared';
import { AICopilotProvider } from '@/contexts/AICopilotContext';
import { AICopilotPanel } from '@/components/ai/AICopilotPanel';
import { CopilotDrawer } from '@/components/ai/CopilotDrawer';
import { FloatingSpeedDial } from '@/components/layout/FloatingSpeedDial';
import { SugerirMejoraHost } from '@/components/propuestas/SugerirMejoraHost';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
import { WaInboxNavProvider } from '@/contexts/WaInboxNavContext';
import { WaChatDockProvider } from '@/contexts/WaChatDockContext';
import { WaChatDock } from '@/components/whatsapp/WaChatDock';
import { WaInboxNavigation } from '@/components/whatsapp/WaInboxNavigation';
import { CallCenterNavigation } from '@/components/callCenter/CallCenterNavigation';
import { AdsNavigation } from '@/components/ads/AdsNavigation';

const BODY_ADMIN_CLASS = 'polaris-admin';
const HTML_ADMIN_CLASS = 'polaris-admin-doc';

/** Botón flotante del copiloto — omnipresente en todo el admin, solo para planes con
 *  la feature 'copilot' (enterprise). Abre el copiloto NUEVO (43 acciones, edge fn
 *  crm-copilot) en un drawer, sin salir de la pantalla. NO es el asistente viejo. */
function CopilotFab() {
  const { isFeatureEnabled } = useFeatureFlags();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  if (!isFeatureEnabled('copilot')) return null;
  // Se oculta el FAB en pantallas full-bleed (y en la propia página del copiloto).
  const HIDDEN = ['/chats/inbox', '/apps/crm/inbox', '/llamadas/dialer', '/apps/crm/copilot'];
  const hideFab = HIDDEN.some((r) => pathname.includes(r));
  return (
    <>
      {!hideFab && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Abrir copiloto"
          title="Copiloto"
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 99990,
            width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            background: 'linear-gradient(135deg, #2563EB, #4F46E5)',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <span style={{ fontSize: 20, lineHeight: 1 }}>✨</span>
        </button>
      )}
      <CopilotDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** Barra de cambios no guardados (estilo Shopify: arriba en la barra) */
function UnsavedChangesBar() {
  const { unsaved } = useUnsavedChanges();
  /**
   * 🪤 El botón no tenía estado de guardando: durante un guardado lento seguía clickeable, y el
   * segundo clic disparaba OTRO guardado en paralelo — en "crear" eso es el registro duplicado.
   * Las páginas encendían su propio `saving` pero nadie lo leía; el spinner tiene que vivir acá,
   * que es donde está el botón que todas comparten.
   */
  const [guardando, setGuardando] = useState(false);
  if (!unsaved) return null;
  return (
    <Box padding="300" paddingBlockEnd="200" background="bg-fill-inverse">
      <InlineStack align="space-between" blockAlign="center" gap="300">
        <Text as="span" variant="bodySm" tone="text-inverse">
          Cambios no guardados
        </Text>
        <InlineStack gap="200" blockAlign="center">
          {/* Descartar también se bloquea durante el guardado: descartar MIENTRAS se guarda es una
              carrera cuyo resultado depende de qué promesa gane. */}
          <Button size="slim" variant="secondary" tone="inverse" disabled={guardando} onClick={unsaved.onDiscard}>
            Descartar
          </Button>
          {/* onSave puede rechazar (ver contrato en UnsavedChangesContext): la página ya
              mostró su toast de error, así que acá solo evitamos el unhandled rejection. */}
          <Button
            size="slim"
            variant="primary"
            tone="inverse"
            loading={guardando}
            onClick={() => {
              setGuardando(true);
              void Promise.resolve(unsaved.onSave())
                .catch(() => {})
                .finally(() => setGuardando(false));
            }}
          >
            Guardar
          </Button>
        </InlineStack>
      </InlineStack>
    </Box>
  );
}

/**
 * Layout admin (estilo Shopify).
 * Frame + Sidebar (Polaris Navigation) + TopBar. AdminGlobalSearch fuera del Frame.
 * polaris-admin en body evita estilos Tailwind. Modal/espaciado override en polaris.css.
 */
export function AppLayout() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { collapsed, isWorkspace, toggle: toggleSidebar } = useSidebarCollapsed();
  const navigate = useNavigate();
  const location = useLocation();
  useSpaInternalNavCapture(navigate);

  // Sidebar contextual: en cualquier ruta /chats/* (inbox, settings, agents, tags,
  // etc.) el nav global se reemplaza por WaInboxNavigation, estilo Chatwoot — el
  // sidebar del módulo solo desaparece al salir de Chats con "Volver".
  // (Se mantiene /whatsapp por las URLs legacy, que igual redirigen a /chats.)
  const isInboxRoute = /\/(chats|whatsapp)(\/|$)/.test(location.pathname);

  // Sidebar contextual de Llamadas (ex Call Center / Voice Commerce). Mismo patrón
  // que Chats: módulo-as-app con su propio mundo. Activo en todo /llamadas/*
  // (marcador, historial, voice-ai, coaching, campañas, supervisión, etc.).
  // Se mantienen los prefijos viejos por las URLs legacy en tránsito al redirect.
  const isCallCenterRoute =
    /\/llamadas(\/|$)/.test(location.pathname) ||
    /\/apps\/call-center(\/|$)/.test(location.pathname) ||
    /\/apps\/crm\/(dialer|calls|voice-ai|call-coaching)(\/|$)/.test(location.pathname);

  // Sidebar contextual del módulo Smarty Ads. Mismo patrón módulo-as-app
  // que Chats / Llamadas: en /ads/* el sidebar global se reemplaza
  // por AdsNavigation con secciones propias (Campañas, Chat senior IA,
  // Crecimiento, Cuenta).
  const isAdsRoute = /\/ads(\/|$)/.test(location.pathname);

  useEffect(() => {
    document.documentElement.classList.add(HTML_ADMIN_CLASS);
    document.body.classList.add(BODY_ADMIN_CLASS);
    return () => {
      document.body.classList.remove(BODY_ADMIN_CLASS);
      document.documentElement.classList.remove(HTML_ADMIN_CLASS);
    };
  }, []);

  // Evitar recarga al elegir 2º+ valor en presets: submit, submit programático, clics y mousedown
  // en <a href="">/# y en button[type=submit] sin data-allow-submit. mousedown evita que el navegador
  // dispare la acción antes del click.
  useEffect(() => {
    const allowSubmit = (form: HTMLFormElement | null) => form?.getAttribute('data-allow-submit') === 'true';

    const submitHandler = (e: Event) => {
      const form =
        e.target instanceof HTMLFormElement
          ? e.target
          : (e.target as Node) && 'closest' in (e.target as Element)
            ? (e.target as Element).closest('form')
            : null;
      if (allowSubmit(form)) return;
      e.preventDefault();
      e.stopPropagation();
    };

    const nativeSubmit = HTMLFormElement.prototype.submit;
    HTMLFormElement.prototype.submit = function (this: HTMLFormElement) {
      if (!allowSubmit(this)) return;
      return nativeSubmit.call(this);
    };

    const preventNavigation = (e: MouseEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      const anchor = target.closest('a');
      if (anchor instanceof HTMLAnchorElement) {
        const href = (anchor.getAttribute('href') ?? '').trim();
        if (href === '' || href === '#') {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }
      const submitBtn = target.closest('button[type="submit"]');
      if (submitBtn) {
        const form = submitBtn.closest('form');
        if (form && !allowSubmit(form)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    document.addEventListener('submit', submitHandler, true);
    document.addEventListener('click', preventNavigation, true);
    document.addEventListener('mousedown', preventNavigation, true);
    return () => {
      document.removeEventListener('submit', submitHandler, true);
      document.removeEventListener('click', preventNavigation, true);
      document.removeEventListener('mousedown', preventNavigation, true);
      HTMLFormElement.prototype.submit = nativeSubmit;
    };
  }, []);

  const topBar = (
    <Box style={{ position: 'relative' }}>
      <Topbar
        onOpenSearch={() => setSearchOpen(true)}
        onNavigationToggle={() => setMobileNavOpen(true)}
        sidebarCollapsed={collapsed}
        onToggleSidebar={toggleSidebar}
      />
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10000,
        }}
      >
        <UnsavedChangesBar />
      </Box>
    </Box>
  );

  return (
    <AICopilotProvider>
      <UnsavedChangesProvider>
        <WaInboxNavProvider>
         {/* Envuelve al Frame para que cualquier pantalla del admin pueda abrir un chat acoplado. */}
         <WaChatDockProvider>
          <Frame
            navigation={
              isInboxRoute
                ? <WaInboxNavigation />
                : isCallCenterRoute
                  ? <CallCenterNavigation />
                  : isAdsRoute
                    ? <AdsNavigation />
                    : <Sidebar onDismiss={() => setMobileNavOpen(false)} />
            }
            topBar={topBar}
            showMobileNavigation={mobileNavOpen}
            onNavigationDismiss={() => setMobileNavOpen(false)}
          >
            <AdminToastProvider>
              <CallCenterProvider>
                {/* Al cambiar de ruta, vuelve el scroll arriba (el admin scrollea en #root, no el window). */}
                <ScrollToTop />
                <Box as="div" paddingBlockEnd={isWorkspace ? '0' : '800'} minHeight="100%">
                  {/* Suspense de nivel de layout: permite lazy-cargar cualquier
                      página del admin sin envolver ruta por ruta. El core (Dashboard,
                      Productos…) sigue eager; canales/apps suspenden brevemente. */}
                  <Suspense fallback={<PageLoader variant="content" />}>
                    <Outlet />
                  </Suspense>
                </Box>
                {/* H1 I4: aislar errores del marcador para no tumbar el resto de la app. */}
                <ErrorBoundarySection sectionName="Marcador" fallback={null}>
                  <FloatingDialerWidget hideFab />
                </ErrorBoundarySection>
                {/*
                  * El dock se MONTA acá adentro —necesita el toast y la sesión— pero se DIBUJA por
                  * portal al body: dentro del Frame, position:fixed se ancla al contenedor y no a
                  * la ventana. Mismo arreglo que usa el marcador.
                  */}
                <ErrorBoundarySection sectionName="Chats acoplados" fallback={null}>
                  <WaChatDock />
                </ErrorBoundarySection>
                {/*
                  * Va DENTRO del Frame aunque lo dispare el speed dial, que está fuera:
                  * el modal usa Polaris y el toast, y los dos necesitan el Frame. El
                  * speed dial solo emite un evento, así que no le importa dónde vive
                  * quien lo escucha. No monta nada hasta que alguien lo abre.
                  */}
                <ErrorBoundarySection sectionName="Sugerir mejora" fallback={null}>
                  <SugerirMejoraHost />
                </ErrorBoundarySection>
              </CallCenterProvider>
            </AdminToastProvider>
          </Frame>
         </WaChatDockProvider>
        </WaInboxNavProvider>
      </UnsavedChangesProvider>
      {/* Fuera del Frame para que position:fixed funcione sobre toda la ventana */}
      <FloatingSpeedDial />
      <AICopilotPanel />
      <CopilotFab />
      <AdminGlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </AICopilotProvider>
  );
}
